import { useEffect, useRef, useState } from "react";
import FeedTable from "../components/FeedTable";
import FilterBar from "../components/FilterBar";
import PageLayout from "../components/PageLayout";
import useFeed from "../hooks/useFeed";
import usePilotName from "../hooks/usePilotName";
import { useLens } from "../lens/LensProvider";
import { FacetToken } from "../types/facets";
import { filtersToTokens, mergeToken, tokensToFilters } from "../utils/facetFilters";
import { FeedFilters } from "../types/feed";

function parseFeedFiltersFromHash(hash: string): FeedFilters {
  const normalized = (hash || "").replace(/^#/, "");
  const [pathPart, queryString = ""] = normalized.split("?");
  const parts = pathPart.split("/").filter(Boolean);

  if (parts.length > 0 && parts[0] !== "feed") {
    return {};
  }

  const query = new URLSearchParams(queryString);
  const filters: FeedFilters = {};

  const assignString = (key: keyof Omit<FeedFilters, "kind">, value: string | null) => {
    if (value) filters[key] = value;
  };

  assignString("pilot", query.get("pilot"));
  assignString("pilot_uid", query.get("pilot_uid"));
  assignString("actor_uid", query.get("actor_uid"));
  assignString("server_id", query.get("server_id"));
  assignString("server_name", query.get("server_name"));
  assignString("unit_type", query.get("unit_type"));
  assignString("weapon", query.get("weapon"));
  assignString("airdrome", query.get("airdrome"));
  assignString("event_type", query.get("event_type"));

  const kind = query.get("kind");
  if (kind === "event" || kind === "proficiency" || kind === "achievement" || kind === "") {
    filters.kind = kind;
  }

  return filters;
}

function isFeedHash(hash: string): boolean {
  const normalized = (hash || "").replace(/^#/, "");
  const [pathPart] = normalized.split("?");
  const parts = pathPart.split("/").filter(Boolean);
  return parts.length === 0 || parts[0] === "feed";
}

function setFeedHash(filters: FeedFilters): void {
  const query = new URLSearchParams();

  const add = (key: keyof FeedFilters) => {
    const value = filters[key];
    if (value) query.set(String(key), String(value));
  };

  add("pilot");
  add("pilot_uid");
  add("actor_uid");
  add("server_id");
  add("server_name");
  add("unit_type");
  add("weapon");
  add("airdrome");
  add("event_type");
  add("kind");

  const nextHash = query.toString() ? `#/feed?${query.toString()}` : "#/feed";
  if (window.location.hash !== nextHash) {
    window.history.replaceState(null, "", nextHash);
  }
}

const FeedPage = () => {
  const { activeUid, viewUid, pinnedUid, setScope, clearViewUid } = useLens();
  const pilotName = usePilotName(activeUid);
  const [initialFilters] = useState<FeedFilters>(() => {
    const parsed = parseFeedFiltersFromHash(window.location.hash);
    return activeUid ? { ...parsed, pilot_uid: activeUid } : parsed;
  });
  const { items, loading, error, hasMore, loadMore, refresh, filters } = useFeed(initialFilters);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [tokens, setTokens] = useState<FacetToken[]>(() => filtersToTokens(initialFilters));
  const tokensRef = useRef(tokens);
  tokensRef.current = tokens;
  const lensUidRef = useRef(activeUid);

  // The lens drives the pilot filter: switching it adds or removes the pilot token and reloads.
  useEffect(() => {
    if (lensUidRef.current === activeUid) return;
    lensUidRef.current = activeUid;
    const current = tokensRef.current;
    const without = current.filter((t) => t.key !== "pilot_uid");
    const next = activeUid ? mergeToken(without, { key: "pilot_uid", value: activeUid, label: pilotName }) : without;
    setTokens(next);
    refresh(tokensToFilters(next));
  }, [activeUid, pilotName, refresh]);

  // Show the pilot's name on the token once it is known.
  useEffect(() => {
    if (!activeUid) return;
    setTokens((prev) => prev.map((t) => (t.key === "pilot_uid" && t.value === activeUid && t.label !== pilotName ? { ...t, label: pilotName } : t)));
  }, [activeUid, pilotName]);

  useEffect(() => {
    if (!isFeedHash(window.location.hash)) return;
    setFeedHash(filters);
  }, [filters]);

  useEffect(() => {
    const onHashChange = () => {
      if (!isFeedHash(window.location.hash)) return;
      const nextFilters = parseFeedFiltersFromHash(window.location.hash);
      setTokens(filtersToTokens(nextFilters));
      refresh(nextFilters);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [refresh]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          loadMore();
        }
      });
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  const handleTokensChange = (nextTokens: FacetToken[]) => {
    const droppedPilot = tokens.some((t) => t.key === "pilot_uid") && !nextTokens.some((t) => t.key === "pilot_uid");
    setTokens(nextTokens);
    refresh(tokensToFilters(nextTokens));
    // Removing the pilot token is the lens toggle: a viewed pilot is released, otherwise back to Everyone.
    if (droppedPilot) {
      if (viewUid && viewUid !== pinnedUid) clearViewUid();
      else setScope("everyone");
    }
  };

  const handleAddFacet = (token: FacetToken) => {
    setTokens((prev) => {
      const next = mergeToken(prev, token);
      refresh(tokensToFilters(next));
      return next;
    });
  };

  const handleRemoveFacet = (token: FacetToken) => {
    setTokens((prev) => {
      const next = prev.filter((t) => !(t.key === token.key && t.value === token.value));
      refresh(tokensToFilters(next));
      return next;
    });
  };

  return (
    <PageLayout route="feed" hero={{
      photo: "flanker", focus: "50% 62%", title: "Activity feed",
      body: "Takeoffs, kills, traps and awards as they happen.",
    }}>
      <section className="panel panel-tight">
        <FilterBar tokens={tokens} onTokensChange={handleTokensChange} />
      </section>

      <section className="panel panel-list">
        {error && (
          <div className="alert">
            <span>{error}</span>
            <button type="button" onClick={() => refresh(filters)}>
              Retry
            </button>
          </div>
        )}

        <FeedTable items={items} onAddFacet={handleAddFacet} onRemoveFacet={handleRemoveFacet} activeTokens={tokens} />

        <div ref={sentinelRef} className="sentinel" aria-hidden="true" />

        <div className="status-area">
          {loading && <span className="status">Loading…</span>}
          {!hasMore && !loading && items.length > 0 && <span className="status">You have reached the end of the feed.</span>}
        </div>
      </section>
    </PageLayout>
  );
};

export default FeedPage;
